import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Loader from "./Loader";
import OneTimePassword from "../models/OneTimePassword";
import ResultStatus from "../classes/ResultStatus";
import Result from "../classes/Result";
import FieldValidation from "../utils/FieldValidation";
import Button from "./Button";
import SignUpRequest from "../models/SignUpRequest";
import ListWrapper from "../utils/ListWrapper";
import CheckboxWithText from "./CheckboxWithText";
import ScrollView2 from "./ScrollView2";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import LabelWithStringField from "./LabelWithStringField";
import { BuildContext } from "../classes/BuildContext";

type _BuilderSignupScreenOnLogin = () => void;

type _BuilderSignupScreenOnEmailVerification = (
  email: string,
  generatedToken: string
) => void;

type _SignUpButtonOnPressed = (d3eState: BuilderSignupScreenRefs) => void;

type _Signup11OnPressed = (d3eState: BuilderSignupScreenRefs) => void;

type _EmailOnChanged = (
  text: string,
  d3eState: BuilderSignupScreenRefs
) => void;

export interface BuilderSignupScreenProps extends BaseUIProps {
  key?: string;
  onLogin: _BuilderSignupScreenOnLogin;
  onEmailVerification: _BuilderSignupScreenOnEmailVerification;
}
/// To store state data for BuilderSignupScreen
class BuilderSignupScreenRefs {
  public clickPrivacy: ClickPrivacyState = new ClickPrivacyState();
  public clickTerms: ClickTermsState = new ClickTermsState();
  public disable: DisableState = new DisableState();
  idScrollController: ui.ScrollController = new ui.ScrollController();
  public signUpButton: SignUpButtonState = new SignUpButtonState();
  public signUpButton1: SignUpButton1State = new SignUpButton1State();
  public signup11: Signup11State = new Signup11State();
}

interface Signup11WithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderSignupScreenRefs;
  _onExistingUserHandler?: _Signup11OnPressed;
}

class Signup11State extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _Signup11WithState extends ObservableComponent<Signup11WithStateProps> {
  signup11FocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: Signup11WithStateProps) {
    super(props);

    this.initState();
  }
  public get signup11(): Signup11State {
    return this.props.d3eState.signup11;
  }
  public get d3eState(): BuilderSignupScreenRefs {
    return this.props.d3eState;
  }
  public get _onExistingUserHandler(): _Signup11OnPressed {
    return this.props._onExistingUserHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("signup11", null, this.signup11);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["signup11", "signup11.", "signup11.focus", "signup11.hover"],
      this.rebuild
    );
  }
  public signup11OnEnter(event): void {
    return this.signup11.setHover(true);
  }
  public signup11OnExit(event): void {
    return this.signup11.setHover(false);
  }
  public signup11OnFocusChange(val): void {
    return this.signup11.setFocus(val);
  }
  public dispose(): void {
    this.signup11.setHover(false);

    this.signup11.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration: this.signup11.focus
        ? cStyle.tButtonUnderlineButtonDecorationOnFocus
        : new ui.BoxDecoration({}),
      disable: this.signup11.disable,
      onPressed: () => {
        this._onExistingUserHandler(this.d3eState);
      },
      onFocusChange: (val) => {
        this.signup11OnFocusChange(val);
      },
      child: TextView({ data: "Log in" }),
      onEnter: (event) => {
        this.signup11OnEnter(event);
      },
      onExit: (event) => {
        this.signup11OnExit(event);
      },
      className: "xcb8",
    });
  }
}
function Signup11WithState(props: Signup11WithStateProps) {
  return React.createElement(_Signup11WithState, props);
}

interface SignUpButton1WithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderSignupScreenRefs;
  agreeMark: boolean;
}

class SignUpButton1State extends ObjectObservable {}

class _SignUpButton1WithState extends ObservableComponent<SignUpButton1WithStateProps> {
  signUpButton1FocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SignUpButton1WithStateProps) {
    super(props);

    this.initState();
  }
  public get agreeMark(): boolean {
    return this.props.agreeMark;
  }
  public get signUpButton1(): SignUpButton1State {
    return this.props.d3eState.signUpButton1;
  }
  public get d3eState(): BuilderSignupScreenRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("signUpButton1", null, this.signUpButton1);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["agreeMark", "signUpButton1"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 383.0 }),
      child: Button({
        disable: true,
        padding: cStyle.tButtonDisabledPrimaryButtonPaddingOn,
        decoration: cStyle.tButtonDisabledPrimaryButtonDecorationOn,
        onFocusChange: (val) => {},
        child: TextView({
          data: "Sign Up",
          states: ui.joinStates({ "data-c0": this.agreeMark }, {}),
          style: new ui.TextStyle({
            color: this.agreeMark ? cStyle.c7 : cStyle.c1,
          }),
          className: "x597 hc",
        }),
      }),
      className: "x719 hc",
    });
  }
}
function SignUpButton1WithState(props: SignUpButton1WithStateProps) {
  return React.createElement(_SignUpButton1WithState, props);
}

interface DisableWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderSignupScreenRefs;
}

class DisableState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _DisableWithState extends ObservableComponent<DisableWithStateProps> {
  disableFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DisableWithStateProps) {
    super(props);

    this.initState();
  }
  public get disable(): DisableState {
    return this.props.d3eState.disable;
  }
  public get d3eState(): BuilderSignupScreenRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("disable", null, this.disable);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["disable", "disable.", "disable.focus", "disable.hover"],
      this.rebuild
    );
  }
  public disableOnEnter(event): void {
    return this.disable.setHover(true);
  }
  public disableOnExit(event): void {
    return this.disable.setHover(false);
  }
  public disableOnFocusChange(val): void {
    return this.disable.setFocus(val);
  }
  public dispose(): void {
    this.disable.setHover(false);

    this.disable.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 383.0 }),
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.disable.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.disable.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.disable.disable,
        onFocusChange: (val) => {
          this.disableOnFocusChange(val);
        },
        child: ui.Wrap({
          alignment: ui.WrapAlignment.center,
          spacing: 5,
          crossAxisAlignment: ui.WrapCrossAlignment.center,
          children: [
            Loader({ className: "hc", key: "0" }),
            TextView({ data: "Signing In", className: "hc", key: "1" }),
          ],
          className: "hc",
        }),
        onEnter: (event) => {
          this.disableOnEnter(event);
        },
        onExit: (event) => {
          this.disableOnExit(event);
        },
      }),
      className: "xeb hc",
    });
  }
}
function DisableWithState(props: DisableWithStateProps) {
  return React.createElement(_DisableWithState, props);
}

interface SignUpButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderSignupScreenRefs;
  _onSignUpFieldValidation?: _SignUpButtonOnPressed;
}

class SignUpButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _SignUpButtonWithState extends ObservableComponent<SignUpButtonWithStateProps> {
  signUpButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SignUpButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get signUpButton(): SignUpButtonState {
    return this.props.d3eState.signUpButton;
  }
  public get d3eState(): BuilderSignupScreenRefs {
    return this.props.d3eState;
  }
  public get _onSignUpFieldValidation(): _SignUpButtonOnPressed {
    return this.props._onSignUpFieldValidation;
  }
  public initState() {
    super.initState();

    this.updateObservable("signUpButton", null, this.signUpButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "signUpButton",
        "signUpButton.",
        "signUpButton.focus",
        "signUpButton.hover",
      ],
      this.rebuild
    );
  }
  public signUpButtonOnEnter(event): void {
    return this.signUpButton.setHover(true);
  }
  public signUpButtonOnExit(event): void {
    return this.signUpButton.setHover(false);
  }
  public signUpButtonOnFocusChange(val): void {
    return this.signUpButton.setFocus(val);
  }
  public dispose(): void {
    this.signUpButton.setHover(false);

    this.signUpButton.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 383.0 }),
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.signUpButton.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.signUpButton.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.signUpButton.disable,
        onPressed: () => {
          this._onSignUpFieldValidation(this.d3eState);
        },
        onFocusChange: (val) => {
          this.signUpButtonOnFocusChange(val);
        },
        child: TextView({ data: "Sign Up", className: "hc" }),
        onEnter: (event) => {
          this.signUpButtonOnEnter(event);
        },
        onExit: (event) => {
          this.signUpButtonOnExit(event);
        },
      }),
      className: "xae0 hc",
    });
  }
}
function SignUpButtonWithState(props: SignUpButtonWithStateProps) {
  return React.createElement(_SignUpButtonWithState, props);
}

interface ClickTermsWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderSignupScreenRefs;
}

class ClickTermsState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _ClickTermsWithState extends ObservableComponent<ClickTermsWithStateProps> {
  clickTermsFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ClickTermsWithStateProps) {
    super(props);

    this.initState();
  }
  public get clickTerms(): ClickTermsState {
    return this.props.d3eState.clickTerms;
  }
  public get d3eState(): BuilderSignupScreenRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("clickTerms", null, this.clickTerms);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["clickTerms", "clickTerms.", "clickTerms.focus", "clickTerms.hover"],
      this.rebuild
    );
  }
  public clickTermsOnEnter(event): void {
    return this.clickTerms.setHover(true);
  }
  public clickTermsOnExit(event): void {
    return this.clickTerms.setHover(false);
  }
  public clickTermsOnFocusChange(val): void {
    return this.clickTerms.setFocus(val);
  }
  public dispose(): void {
    this.clickTerms.setHover(false);

    this.clickTerms.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      disable: this.clickTerms.disable,
      onFocusChange: (val) => {
        this.clickTermsOnFocusChange(val);
      },
      child: TextView({
        data: "Terms of Services",
        style: new ui.TextStyle({ fontSize: 13.0 }),
        className: "x6a9 hc",
      }),
      onEnter: (event) => {
        this.clickTermsOnEnter(event);
      },
      onExit: (event) => {
        this.clickTermsOnExit(event);
      },
      className: "x60 hc",
    });
  }
}
function ClickTermsWithState(props: ClickTermsWithStateProps) {
  return React.createElement(_ClickTermsWithState, props);
}

interface ClickPrivacyWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderSignupScreenRefs;
}

class ClickPrivacyState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _ClickPrivacyWithState extends ObservableComponent<ClickPrivacyWithStateProps> {
  clickPrivacyFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ClickPrivacyWithStateProps) {
    super(props);

    this.initState();
  }
  public get clickPrivacy(): ClickPrivacyState {
    return this.props.d3eState.clickPrivacy;
  }
  public get d3eState(): BuilderSignupScreenRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("clickPrivacy", null, this.clickPrivacy);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "clickPrivacy",
        "clickPrivacy.",
        "clickPrivacy.focus",
        "clickPrivacy.hover",
      ],
      this.rebuild
    );
  }
  public clickPrivacyOnEnter(event): void {
    return this.clickPrivacy.setHover(true);
  }
  public clickPrivacyOnExit(event): void {
    return this.clickPrivacy.setHover(false);
  }
  public clickPrivacyOnFocusChange(val): void {
    return this.clickPrivacy.setFocus(val);
  }
  public dispose(): void {
    this.clickPrivacy.setHover(false);

    this.clickPrivacy.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      disable: this.clickPrivacy.disable,
      onFocusChange: (val) => {
        this.clickPrivacyOnFocusChange(val);
      },
      child: TextView({
        data: "Privacy Policy ",
        style: new ui.TextStyle({ fontSize: 13.0 }),
        className: "x3b hc",
      }),
      onEnter: (event) => {
        this.clickPrivacyOnEnter(event);
      },
      onExit: (event) => {
        this.clickPrivacyOnExit(event);
      },
      className: "x25 hc",
    });
  }
}
function ClickPrivacyWithState(props: ClickPrivacyWithStateProps) {
  return React.createElement(_ClickPrivacyWithState, props);
}

class _BuilderSignupScreenState extends ObservableComponent<BuilderSignupScreenProps> {
  d3eState: BuilderSignupScreenRefs = new BuilderSignupScreenRefs();
  agreeMark: boolean = false;
  hitRequest: boolean = false;
  errorMessages: Array<string> = ListWrapper.widget(this, "errorMessages");
  showErrors: boolean = false;
  signUpRequest: SignUpRequest = null;
  isNotValidEmail: boolean = false;
  otpError: boolean = false;
  emailInfo: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuilderSignupScreenProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.computeSignUpRequest();

    this.on(
      [
        "agreeMark",
        "emailInfo",
        "errorMessages",
        "hitRequest",
        "isNotValidEmail",
        "otpError",
        "showErrors",
        "signUpRequest",
        "signUpRequest.email",
      ],
      this.rebuild
    );
  }
  public setAgreeMark(val: boolean): void {
    let isValChanged: boolean = this.agreeMark !== val;

    if (!isValChanged) {
      return;
    }

    this.agreeMark = val;

    this.fire("agreeMark", this);
  }
  public setHitRequest(val: boolean): void {
    let isValChanged: boolean = this.hitRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.hitRequest = val;

    this.fire("hitRequest", this);
  }
  public setErrorMessages(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.errorMessages,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.errorMessages.clear();

    this.errorMessages.addAll(val);

    this.fire("errorMessages", this);
  }
  public addToErrorMessages(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errorMessages.contains(val)) this.errorMessages.add(val);
    } else {
      this.errorMessages.remove(this.errorMessages.elementAt(index));

      this.errorMessages.add(val);
    }

    this.fire("errorMessages", this, val, true);
  }
  public removeFromErrorMessages(val: string): void {
    this.errorMessages.remove(val);

    this.fire("errorMessages", this, val, false);
  }
  public setShowErrors(val: boolean): void {
    let isValChanged: boolean = this.showErrors !== val;

    if (!isValChanged) {
      return;
    }

    this.showErrors = val;

    this.fire("showErrors", this);
  }
  public setSignUpRequest(val: SignUpRequest): void {
    let isValChanged: boolean = this.signUpRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("signUpRequest", this.signUpRequest, val);

    this.signUpRequest = val;

    this.fire("signUpRequest", this);
  }
  public computeSignUpRequest = (): void => {
    try {
      this.setSignUpRequest(new SignUpRequest());
    } catch (exception) {
      console.log(
        " exception in computeSignUpRequest : " + exception.toString()
      );

      this.setSignUpRequest(null);
    }
  };
  public setIsNotValidEmail(val: boolean): void {
    let isValChanged: boolean = this.isNotValidEmail !== val;

    if (!isValChanged) {
      return;
    }

    this.isNotValidEmail = val;

    this.fire("isNotValidEmail", this);
  }
  public setOtpError(val: boolean): void {
    let isValChanged: boolean = this.otpError !== val;

    if (!isValChanged) {
      return;
    }

    this.otpError = val;

    this.fire("otpError", this);
  }
  public setEmailInfo(val: boolean): void {
    let isValChanged: boolean = this.emailInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.emailInfo = val;

    this.fire("emailInfo", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ScrollView2({
      child: ui.Column({
        children: [
          ui.AssetImage({
            path: "images/signupimage.png",
            width: 313,
            height: 230.5,
            className: "x23 hc vc",
            key: "0",
          }),
          TextView({
            data: "Try D3E For Free",
            style: new ui.TextStyle({
              color: cStyle.tTextViewBoldHeadingColorOn,
              fontSize: cStyle.tTextViewBoldHeadingFontSizeOn,
              fontWeight: cStyle.tTextViewBoldHeadingFontWeightOn,
            }),
            className: "x30 hc",
            key: "1",
          }),
          TextView({
            data: "Please use your work email address so we can connect you with your team in D3E",
            textAlign: cStyle.tTextViewParagraphCenterTextAlignOn,
            style: new ui.TextStyle({
              color: cStyle.tTextViewParagraphCenterColorOn,
              fontSize: cStyle.tTextViewParagraphCenterFontSizeOn,
            }),
            className: "x671 hc",
            key: "2",
          }),
          ui.Column({
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              LabelWithStringField({
                name: "Email Address",
                value: this.signUpRequest.email,
                placeHolder: "YourName@Company.com",
                help: "Your work email address",
                onChanged: (text) => {
                  this.emailonChanged(text, this.d3eState);

                  this.setStateEmail(text, this.d3eState);
                },
                errors: this.emailInfo
                  ? ["Please enter an email address"]
                  : this.isNotValidEmail
                  ? ["Please enter valid email address"]
                  : [],
                className: "xe23 hc h",
                key: "0",
              }),
            ],
            className: "xb42 hc",
            key: "3",
          }),
          ui.Container({ className: "xfb hc vc", key: "4" }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              ui.Wrap({
                spacing: 5.0,
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  CheckboxWithText({
                    value: this.agreeMark,
                    title: "By Signing up, I agree to the D3E",
                    activeColor:
                      cStyle.tCheckboxWithTextActiveCheckboxWithTextActiveColorOn,
                    onChanged: (value) => {
                      this.setStateAgreeMarkChecking(value, this.d3eState);
                    },
                    className: "x96 hc h",
                    key: "0",
                  }),
                  ClickPrivacyWithState({ d3eState: this.d3eState, key: "1" }),
                  TextView({
                    data: "and ",
                    textAlign: ui.TextAlign.start,
                    className: "hc",
                    key: "2",
                  }),
                  ClickTermsWithState({ d3eState: this.d3eState, key: "3" }),
                ],
                className: "x59 hc",
                key: "0",
              }),
            ],
            className: "xb6 hc h",
            key: "5",
          }),
          this.showErrors
            ? ui.Column({
                children: [
                  this.errorMessages.expand((error) => [
                    TextView({
                      data: error,
                      textAlign: cStyle.tTextViewErrorMessageTextAlignOn,
                      style: new ui.TextStyle({
                        color: cStyle.tTextViewErrorMessageColorOn,
                        fontSize: cStyle.tTextViewErrorMessageFontSizeOn,
                      }),
                      className: "xcfc hc",
                      key: error?.toString(),
                    }),
                  ]),
                ],
                className: "xe5 hc",
              })
            : [],
          this.otpError
            ? TextView({
                data: "Something went wrong",
                textAlign: cStyle.tTextViewErrorMessageTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewErrorMessageColorOn,
                  fontSize: cStyle.tTextViewErrorMessageFontSizeOn,
                }),
                className: "x36b hc",
              })
            : [],
          ui.Container({ className: "x0f hc vc", key: "8" }),
          this.agreeMark
            ? !this.hitRequest
              ? SignUpButtonWithState({
                  d3eState: this.d3eState,
                  _onSignUpFieldValidation: this.onSignUpFieldValidation,
                })
              : DisableWithState({ d3eState: this.d3eState })
            : SignUpButton1WithState({
                d3eState: this.d3eState,
                agreeMark: this.agreeMark,
              }),
          ui.Container({ className: "xa3 hc vc", key: "10" }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              TextView({
                data: "Already a User?  ",
                className: "x21",
                key: "0",
              }),
              Signup11WithState({
                d3eState: this.d3eState,
                _onExistingUserHandler: this.onExistingUserHandler,
                key: "1",
              }),
            ],
            className: "xaf9 hc h",
            key: "11",
          }),
        ],
        className: ui.join(
          this.props.className,
          "BuilderSignupScreen xda1 hc h"
        ),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "BuilderSignupScreen xda1 hc h",
      controller: this.d3eState.idScrollController,
    });
  }
  public onExistingUserHandler = (d3eState: BuilderSignupScreenRefs): void => {
    this.onLogin();
  };
  public setStateEmail = (
    value: string,
    d3eState: BuilderSignupScreenRefs
  ): void => {
    this.setIsNotValidEmail(false);

    this.setEmailInfo(false);

    this.signUpRequest.setEmail(value);
  };
  public setStateAgreeMarkChecking = (
    value: boolean,
    d3eState: BuilderSignupScreenRefs
  ): void => {
    this.setAgreeMark(value);
  };
  public registerNewUser = async (): Promise<void> => {
    this.setEmailInfo(false);

    if (FieldValidation.isEmail(this.signUpRequest.email)) {
      this.setIsNotValidEmail(false);

      this.setHitRequest(true);

      this.setShowErrors(false);

      let result: Result<SignUpRequest> = await this.signUpRequest.save();

      this.setHitRequest(false);

      if (result.status === ResultStatus.Success) {
        let otp: OneTimePassword = new OneTimePassword({
          input: this.signUpRequest.email.toLowerCase(),
          inputType: "email",
          userType: "Developer",
        });

        let result1: Result<OneTimePassword> = await otp.save();

        if (result1.status === ResultStatus.Success) {
          this.onEmailVerification(this.signUpRequest.email, otp.token);
        } else {
          this.setOtpError(true);
        }
      } else {
        let newList: Array<string> = result.errors
          .where((x) => x !== null)
          .toList();

        if (newList.isNotEmpty) {
          this.setShowErrors(true);

          this.setErrorMessages(newList);
        }

        this.setOtpError(true);
      }
    } else if (
      this.signUpRequest.email.isNotEmpty &&
      FieldValidation.isEmail(this.signUpRequest.email)
    ) {
      this.setIsNotValidEmail(true);
    }
  };
  public onSignUpFieldValidation = (
    d3eState: BuilderSignupScreenRefs
  ): void => {
    this.setEmailInfo(false);

    this.setIsNotValidEmail(false);

    if (this.signUpRequest.email === "") {
      this.setEmailInfo(true);
    } else {
      this.setIsNotValidEmail(
        !FieldValidation.isEmail(this.signUpRequest.email)
      );

      if (!(this.isNotValidEmail || !this.agreeMark)) {
        this.registerNewUser();
      }
    }
  };
  public get onLogin(): _BuilderSignupScreenOnLogin {
    return this.props.onLogin;
  }
  public get onEmailVerification(): _BuilderSignupScreenOnEmailVerification {
    return this.props.onEmailVerification;
  }
  public emailonChanged = (
    val: string,
    d3eState: BuilderSignupScreenRefs
  ): void => {
    this.signUpRequest.setEmail(val);
  };
  public get clickPrivacy() {
    return this.d3eState.clickPrivacy;
  }
  public get clickTerms() {
    return this.d3eState.clickTerms;
  }
  public get disable() {
    return this.d3eState.disable;
  }
  public get signUpButton() {
    return this.d3eState.signUpButton;
  }
  public get signUpButton1() {
    return this.d3eState.signUpButton1;
  }
  public get signup11() {
    return this.d3eState.signup11;
  }
}
export default function BuilderSignupScreen(props: BuilderSignupScreenProps) {
  return React.createElement(_BuilderSignupScreenState, props);
}
